import toast from 'react-hot-toast';
import { createElement } from 'react';
import { UnifiedToast, type ToastVariant } from '@/components/ui/UnifiedToast';
import { getToastDescription } from './toast-descriptions';
import { shouldRetainToast } from './retention';
import { recordRetainedToast } from './toastSink';

export interface ShowToastOptions {
  variant: ToastVariant;
  title: string;
  description?: string;
  categoryLabel?: string;
  icon?: React.ReactNode;
  accentColor?: string;
  avatar?: string;
  sender?: string;
  actionLabel?: string;
  onAction?: () => void;
  onClick?: () => void;
  showCloseButton?: boolean;
  duration?: number;
  id?: string;
  /**
   * Set `false` ONLY for one of the five excluded acknowledgement classes in
   * retention.ts, so the exclusion is greppable at its call site. Ignored for
   * `error` and `warning`: a call site cannot hide a failure from the bell.
   */
  retain?: boolean;
  /**
   * Another path already writes this notification's record (the unified hook's
   * durable and explicitly-scoped writes). Prevents a second bell entry; unlike
   * `retain` it is honoured for failures, because it never removes the record.
   */
  alreadyRecorded?: boolean;
  position?: 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right';
}

const DEFAULT_DURATIONS: Record<ToastVariant, number> = {
  success: 4000,
  error: 6000,
  info: 4000,
  warning: 5000,
  loading: Infinity,
  message: 5000,
  achievement: 5000,
};

export function showUnifiedToast(options: ShowToastOptions): string {
  const {
    variant,
    title,
    description: explicitDescription,
    categoryLabel,
    icon,
    accentColor,
    avatar,
    sender,
    actionLabel,
    onAction,
    onClick,
    showCloseButton,
    duration,
    id,
    position,
    retain,
    alreadyRecorded,
  } = options;

  // Auto-lookup description from registry if none explicitly provided
  const description = explicitDescription || getToastDescription(title);

  // EVERY TOAST LEAVES A RECORD UNLESS IT EXPLICITLY OPTS OUT.
  //
  // This is the seam the owner's report landed on: "they just come and go and
  // you have no idea what they said once they are gone." Three of the four
  // toast entry points converge here, and this function had no store coupling
  // at all — the persistence decision hung off ONE of the four
  // (persistScopedNotification) behind a scope default that resolved to
  // "persist nothing". Putting retention here makes it a property of a toast
  // rather than a privilege a call site requests, so no call site has to be
  // rewritten to gain a record. See retention.ts for the rule and the five
  // excluded classes.
  if (shouldRetainToast({ variant, title, retain, alreadyRecorded })) {
    recordRetainedToast({
      variant,
      title,
      description,
      id,
      sourcePath: typeof window !== 'undefined' ? window.location.pathname : undefined,
      raisedAt: new Date().toISOString(),
    });
  }

  return toast.custom(
    (t) =>
      createElement(UnifiedToast, {
        t,
        variant,
        title,
        description,
        categoryLabel,
        icon,
        accentColor,
        avatar,
        sender,
        actionLabel,
        onAction,
        onClick,
        showCloseButton,
      }),
    {
      duration: duration ?? DEFAULT_DURATIONS[variant],
      id,
      position: position ?? 'bottom-right',
    }
  );
}
